import { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import { KWS_MODEL_DIR, ASR_MODEL_DIR } from "../services/voiceConfig";
import { playWakeCue, playIntentOkCue, playIntentUnknownCue, playWakeWordCue, playIntentConfirmationCue } from "../utils/audioCues";
import { extractCatchFromTranscript } from '../utils/extractCatchFromTranscript';

const Voice = registerPlugin("Voice");

const STORAGE_KEY = "the99.voiceCommands.enabled.v1";

const safeHaptic = async (fn) => {
  try { await fn(); } catch (_) { /* ignore */ }
};

/**
 * Phase 2a — useVoiceCommands hook.
 *
 * Manages the sherpa-onnx (KeywordSpotter + OnlineRecognizer) listening loop and
 * dispatches recognized intents to per-command callbacks supplied by the screen.
 *
 * Usage:
 *   const voice = useVoiceCommands({
 *     onStartRecording: () => gp.startRecording(),
 *     onStopRecording:  () => gp.stopRecording(),
 *     onUpload:         () => handleUpload(),
 *   });
 *   voice.toggle(); // user-driven enable/disable
 *
 * State:
 *   supported  — true on native Android
 *   enabled    — user toggle (persisted)
 *   listening  — backend reports active service
 *   state      — listening_wake | listening_command | stopped
 *   lastEvent  — most recent event for UI display
 *   error      — last error message
 */

/**
 * Phase 2b: Add dictation mode for catch logging.
 * Usage:
 *   const voice = useVoiceCommands({
 *     onDictationResult: (parsed, raw) => ...
 *   });
 */
export default function useVoiceCommands({
  onStartRecording,
  onStopRecording,
  onUpload,
  onDictationResult,
  onDiscard,
  onStatus,
  onQuietMode,
} = {}) {
  const supported = Capacitor.getPlatform() === "android" && Capacitor.isNativePlatform();
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch (_) { return false; }
  });
  const [state, setState] = useState("stopped");
  const [lastEvent, setLastEvent] = useState(null);
  const [error, setError] = useState(null);

  // Keep latest callbacks in refs so the listener bindings remain stable.
  const cbRef = useRef({ onStartRecording, onStopRecording, onUpload, onDictationResult, onDiscard, onStatus, onQuietMode });
  useEffect(() => {
    cbRef.current = { onStartRecording, onStopRecording, onUpload, onDictationResult, onDiscard, onStatus, onQuietMode };
  }, [onStartRecording, onStopRecording, onUpload, onDictationResult, onDiscard, onStatus, onQuietMode]);

  // Wire native events once.
  useEffect(() => {
    if (!supported) return undefined;
    const handles = [];
    handles.push(Voice.addListener("stateChange", (e) => {
      if (e?.state) setState(e.state);
    }));
    handles.push(Voice.addListener("wake", (e) => {
      setLastEvent({ type: "wake", at: Date.now(), data: e });
      playWakeWordCue(); // New: distinct wake-word cue
      safeHaptic(() => Haptics.impact({ style: ImpactStyle.Light }));
    }));
    handles.push(Voice.addListener("command", (e) => {
      setLastEvent({ type: "command", at: Date.now(), data: e });
      const intent = e?.intent;
      const cbs = cbRef.current || {};
      if (intent === "unknown" || !intent) {
        playIntentUnknownCue();
        safeHaptic(() => Haptics.notification({ type: NotificationType.Warning }));
        return;
      }
      playIntentConfirmationCue(); // New: intent-confirmation cue
      safeHaptic(() => Haptics.impact({ style: ImpactStyle.Medium }));
      try {
        if (intent === "startRecording") cbs.onStartRecording?.();
        else if (intent === "stopRecording") cbs.onStopRecording?.();
        else if (intent === "upload") cbs.onUpload?.();
        else if (intent === "logCatch") startDictation();
        else if (intent === "discard") cbs.onDiscard?.();
        else if (intent === "status") cbs.onStatus?.();
        else if (intent === "quietMode") cbs.onQuietMode?.();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[voice] command handler threw:", err);
      }
    }));
    handles.push(Voice.addListener("dictation", (e) => {
      const raw = e?.transcript || "";
      const reason = e?.reason || "endpoint";
      const parsed = extractCatchFromTranscript(raw);
      setDictationActive(false);
      setDictationTranscript(raw);
      setLastEvent({ type: "dictation", at: Date.now(), data: e });
      try { cbRef.current.onDictationResult?.(parsed, raw, reason); }
      catch (err) { console.warn("[voice] onDictationResult threw:", err); }
    }));
    handles.push(Voice.addListener("error", (e) => {
      setError(e?.message || "voice error");
    }));
    return () => {
      Promise.all(handles.map(async (h) => {
        try { (await h)?.remove?.(); } catch (_) {}
      }));
    };
  }, [supported]);

  // Dictation state/logic
  const [dictationActive, setDictationActive] = useState(false);
  const [dictationTranscript, setDictationTranscript] = useState("");

  const startDictation = useCallback(async () => {
    if (!supported) {
      setError("Dictation requires native Android build");
      return false;
    }
    try {
      setError(null);
      const perm = await Voice.requestPermission();
      if (!perm?.granted) {
        setError("Microphone permission denied");
        return false;
      }
      setDictationTranscript("");
      setDictationActive(true);
      await Voice.startDictation({
        asrModelDir: ASR_MODEL_DIR,
      });
      return true;
    } catch (e) {
      setError(e?.message || String(e));
      setDictationActive(false);
      return false;
    }
  }, [supported]);

  const cancelDictation = useCallback(async () => {
    if (!supported) return;
    try { await Voice.stopDictation?.(); }
    catch (_) { /* ignore */ }
    setDictationActive(false);
  }, [supported]);

  const start = useCallback(async () => {
    if (!supported) return;
    try {
      setError(null);
      const perm = await Voice.requestPermission();
      if (!perm?.granted) {
        setError("Microphone permission denied");
        return;
      }
      await Voice.start({
        kwsModelDir: KWS_MODEL_DIR,
        asrModelDir: ASR_MODEL_DIR,
      });
      // Audible confirmation that the listener is now active.
      playWakeCue();
    } catch (e) {
      setError(e?.message || String(e));
    }
  }, [supported]);

  const stop = useCallback(async () => {
    if (!supported) return;
    try { await Voice.stop(); } catch (_) {}
  }, [supported]);

  // Drive service to match enabled flag.
  useEffect(() => {
    if (!supported) return;
    if (enabled) start();
    else stop();
  }, [enabled, supported, start, stop]);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch (_) {}
      return next;
    });
  }, []);

  return {
    supported,
    enabled,
    listening: state !== "stopped",
    state,
    lastEvent,
    error,
    toggle,
    start,
    stop,
    dictationActive,
    dictationTranscript,
    startDictation,
    cancelDictation,
  };
}
